import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { Edit as EditIcon, Groups as GroupsIcon } from '@mui/icons-material';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import ImageUpload from '../components/ImageUpload';
import { normalizeImagePath, toAdminImageUrl } from '../utils/imageUploadCore';

interface CompanyBrand {
  primaryColor: string;
  shellBackgroundColor: string;
  logoUrl: string | null;
}

interface Company {
  id: string;
  name: string;
  slug: string;
  isActive: boolean;
  studentsCount: number;
  createdAt: string;
  brandPrimaryColor?: string;
  brandShellBackgroundColor?: string;
  brandLogoUrl?: string | null;
  brand?: CompanyBrand;
}

const DEFAULT_PRIMARY = '#49FFC1';
const DEFAULT_SHELL = '#13322B';

const extractUploadFilename = (imageUrl: string): string => {
  const normalized = normalizeImagePath(imageUrl);
  if (normalized.startsWith('/api/uploads/images/')) {
    return normalized.replace(/^\/api\/uploads\//, '');
  }
  if (normalized.startsWith('/api/uploads/')) {
    return normalized.replace(/^\/api\/uploads\//, '');
  }
  return normalized.replace(/^\/uploads\//, '');
};

const Companies: React.FC = () => {
  const navigate = useNavigate();
  const [companies, setCompanies] = useState<Company[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingCompany, setEditingCompany] = useState<Company | null>(null);
  const [form, setForm] = useState({
    name: '',
    slug: '',
    isActive: true,
    brandPrimaryColor: DEFAULT_PRIMARY,
    brandShellBackgroundColor: DEFAULT_SHELL,
    brandLogoUrl: null as string | null,
  });

  const token = localStorage.getItem('admin_token');
  const apiBaseUrl = process.env.REACT_APP_API_URL;

  const loadCompanies = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await axios.get(`${apiBaseUrl}/companies`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setCompanies(response.data?.companies || []);
    } catch (err: any) {
      setError(err?.response?.data?.error || 'Impossible de charger les entreprises');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void loadCompanies();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openCreateDialog = () => {
    setEditingCompany(null);
    setForm({
      name: '',
      slug: '',
      isActive: true,
      brandPrimaryColor: DEFAULT_PRIMARY,
      brandShellBackgroundColor: DEFAULT_SHELL,
      brandLogoUrl: null,
    });
    setDialogOpen(true);
  };

  const openEditDialog = (company: Company) => {
    setEditingCompany(company);
    setForm({
      name: company.name,
      slug: company.slug,
      isActive: company.isActive,
      brandPrimaryColor: company.brand?.primaryColor || company.brandPrimaryColor || DEFAULT_PRIMARY,
      brandShellBackgroundColor:
        company.brand?.shellBackgroundColor || company.brandShellBackgroundColor || DEFAULT_SHELL,
      brandLogoUrl: company.brand?.logoUrl || company.brandLogoUrl || null,
    });
    setDialogOpen(true);
  };

  const handleLogoUploaded = async (imageUrl: string) => {
    if (!editingCompany) return;
    setError('');
    try {
      const filename = extractUploadFilename(imageUrl);
      const response = await axios.put(
        `${apiBaseUrl}/companies/${editingCompany.id}`,
        { brandLogoFilename: filename },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setForm((prev) => ({
        ...prev,
        brandLogoUrl:
          response.data?.company?.brandLogoUrl ||
          response.data?.company?.brand?.logoUrl ||
          normalizeImagePath(imageUrl),
      }));
      setSuccess('Logo mis a jour');
      await loadCompanies();
    } catch (err: any) {
      setError(err?.response?.data?.error || 'Impossible de televerser le logo');
    }
  };

  const handleSave = async () => {
    if (!form.name.trim()) {
      setError('Le nom est obligatoire');
      return;
    }
    try {
      setError('');
      setSuccess('');
      if (editingCompany) {
        await axios.put(
          `${apiBaseUrl}/companies/${editingCompany.id}`,
          {
            name: form.name,
            isActive: form.isActive,
            brandPrimaryColor: form.brandPrimaryColor,
            brandShellBackgroundColor: form.brandShellBackgroundColor,
          },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setSuccess('Entreprise mise a jour');
      } else {
        await axios.post(
          `${apiBaseUrl}/companies`,
          {
            name: form.name,
            slug: form.slug.trim() || undefined,
          },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setSuccess('Entreprise creee');
      }
      setDialogOpen(false);
      await loadCompanies();
    } catch (err: any) {
      setError(err?.response?.data?.error || "Impossible d'enregistrer l'entreprise");
    }
  };

  const handleClearLogo = async () => {
    if (!editingCompany) return;
    try {
      await axios.put(
        `${apiBaseUrl}/companies/${editingCompany.id}`,
        { clearBrandLogo: true },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setForm((prev) => ({ ...prev, brandLogoUrl: null }));
      setSuccess('Logo supprime');
      await loadCompanies();
    } catch (err: any) {
      setError(err?.response?.data?.error || 'Impossible de supprimer le logo');
    }
  };

  const resolveLogoPreviewUrl = (logoUrl: string | null): string | null => {
    if (!logoUrl) return null;
    return toAdminImageUrl(logoUrl);
  };

  return (
    <Container maxWidth="lg">
      <Box mb={3} display="flex" justifyContent="space-between" alignItems="center">
        <Box>
          <Typography variant="h4" fontWeight="bold">Entreprises</Typography>
          <Typography color="text.secondary">
            Gestion des entreprises et personnalisation de l&apos;app apprenant
          </Typography>
        </Box>
        <Button variant="contained" onClick={openCreateDialog}>Ajouter une entreprise</Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>{success}</Alert>}

      <Stack spacing={1.5}>
        {!loading && companies.length === 0 && (
          <Card><CardContent><Typography>Aucune entreprise pour le moment.</Typography></CardContent></Card>
        )}
        {companies.map((company) => (
          <Card key={company.id}>
            <CardContent sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Box display="flex" alignItems="center" gap={2}>
                <Box
                  sx={{
                    width: 40,
                    height: 40,
                    borderRadius: '50%',
                    overflow: 'hidden',
                    bgcolor: company.brand?.shellBackgroundColor || DEFAULT_SHELL,
                    border: `2px solid ${company.brand?.primaryColor || DEFAULT_PRIMARY}`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  {company.brand?.logoUrl ? (
                    <Box
                      component="img"
                      src={resolveLogoPreviewUrl(company.brand.logoUrl) || undefined}
                      alt=""
                      sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
                    />
                  ) : (
                    <Typography variant="caption" color="white">{company.name.slice(0, 1)}</Typography>
                  )}
                </Box>
                <Box>
                  <Typography fontWeight={700}>{company.name}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    slug: {company.slug} | apprenants: {company.studentsCount} | login: /login?company={company.slug}
                  </Typography>
                </Box>
              </Box>
              <Box display="flex" gap={1}>
                <Tooltip title="Voir les apprenants">
                  <IconButton onClick={() => navigate(`/companies/${company.id}/students`)}>
                    <GroupsIcon />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Modifier">
                  <IconButton onClick={() => openEditDialog(company)}>
                    <EditIcon />
                  </IconButton>
                </Tooltip>
              </Box>
            </CardContent>
          </Card>
        ))}
      </Stack>

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editingCompany ? 'Modifier entreprise' : 'Nouvelle entreprise'}</DialogTitle>
        <DialogContent>
          <TextField
            margin="dense"
            label="Nom"
            value={form.name}
            onChange={(e) => setForm((prev) => ({ ...prev, name: e.target.value }))}
            fullWidth
          />
          <TextField
            margin="dense"
            label="Slug (optionnel)"
            value={form.slug}
            onChange={(e) => setForm((prev) => ({ ...prev, slug: e.target.value }))}
            fullWidth
            disabled={Boolean(editingCompany)}
            helperText={
              editingCompany
                ? 'Le slug est definitif et ne peut pas etre modifie'
                : 'Utilise pour /login?company=slug — genere automatiquement si vide'
            }
          />
          {editingCompany && (
            <>
              <Box mt={2}>
                <Typography variant="subtitle2" gutterBottom>Couleur primaire (app apprenant)</Typography>
                <Box display="flex" gap={1} alignItems="center">
                  <TextField
                    type="color"
                    value={form.brandPrimaryColor}
                    onChange={(e) => setForm((prev) => ({ ...prev, brandPrimaryColor: e.target.value.toUpperCase() }))}
                    sx={{ width: 72 }}
                  />
                  <TextField
                    value={form.brandPrimaryColor}
                    onChange={(e) => setForm((prev) => ({ ...prev, brandPrimaryColor: e.target.value }))}
                    fullWidth
                  />
                </Box>
              </Box>
              <Box mt={2}>
                <Typography variant="subtitle2" gutterBottom>Fond sombre (shell app)</Typography>
                <Box display="flex" gap={1} alignItems="center">
                  <TextField
                    type="color"
                    value={form.brandShellBackgroundColor}
                    onChange={(e) =>
                      setForm((prev) => ({ ...prev, brandShellBackgroundColor: e.target.value.toUpperCase() }))
                    }
                    sx={{ width: 72 }}
                  />
                  <TextField
                    value={form.brandShellBackgroundColor}
                    onChange={(e) => setForm((prev) => ({ ...prev, brandShellBackgroundColor: e.target.value }))}
                    fullWidth
                  />
                </Box>
              </Box>
              <ImageUpload
                label="Logo carre (rond en haut a gauche apprenant)"
                currentImage={form.brandLogoUrl || undefined}
                onImageUploaded={(imageUrl) => void handleLogoUploaded(imageUrl)}
                onImageRemoved={() => void handleClearLogo()}
                maxSize={2}
                forcedAspectRatio={1}
                hideAspectRatioSelector
              />
              <Box mt={1}>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>Actif</Typography>
                <Switch
                  checked={form.isActive}
                  onChange={(e) => setForm((prev) => ({ ...prev, isActive: e.target.checked }))}
                />
              </Box>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Annuler</Button>
          <Button variant="contained" onClick={handleSave}>Enregistrer</Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default Companies;
